import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Icon,
  Dropdown,
  Input,
  DropdownItem,
} from 'design-system-web';
import { Unit, SourceType } from 'graphql-query-contracts';
import { Item } from 'components/form/Dropdown/Dropdown';
import type { LeaseItem } from '../../hooks/useEditTenantLeases';
import styles from './styles.scss';

import { SourceTypeLabel } from '../../../../../../../../type-maps/source-type-label.map';

export type EditTenantLeaseItemProps = {
  leaseItem: LeaseItem;
  editLeaseItem: (leaseItem: LeaseItem) => void;
  removeLeaseItem: (leaseItemId: string) => void;
};

type LeaseItemErrorField = keyof LeaseItem['errorMessage'];

function isDropdownItem(value: unknown): value is Item<string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    typeof value.value === 'string'
  );
}

const externalIdSourceOptions: DropdownItem<SourceType>[] = [
  {
    label: SourceTypeLabel[SourceType.SourceTypeActivate],
    value: SourceType.SourceTypeActivate,
    isDisabled: true,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeCbre],
    value: SourceType.SourceTypeCbre,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeMri],
    value: SourceType.SourceTypeMri,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeSfdc],
    value: SourceType.SourceTypeSfdc,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeVcm],
    value: SourceType.SourceTypeVcm,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeYardi],
    value: SourceType.SourceTypeYardi,
  },
];

export const EditTenantLeaseItem = ({
  leaseItem,
  editLeaseItem,
  removeLeaseItem,
}: EditTenantLeaseItemProps) => {
  const { t } = useTranslation();

  const suiteDropdownItems = leaseItem.availableSuites.map((suite: Unit) => ({
    label: suite.name || suite.id,
    value: suite.id,
  }));

  const handleOnChange = (
    fieldName: string,
    fieldValue: string | Item<string> | null
  ) => {
    let value = fieldValue;

    if (isDropdownItem(fieldValue)) {
      value = fieldValue.value;
    }

    const updatedLeaseItem = {
      ...leaseItem,
      [fieldName]: value,
    };

    editLeaseItem(updatedLeaseItem);
  };

  const handleOnChangeSuite = (value: Item<string> | null) => {
    const valueToSave = value?.value || '';

    const updatedLeaseItem = {
      ...leaseItem,
      suiteId: valueToSave,
    };

    editLeaseItem(updatedLeaseItem);
  };

  const handleRemove = () => {
    removeLeaseItem(leaseItem.id);
  };

  const getErrorMessages = (fieldName: LeaseItemErrorField) => {
    const errorMessage = leaseItem.errorMessage[fieldName];

    if (errorMessage) {
      return [errorMessage];
    }

    return [];
  };

  const globalErrorMessage = leaseItem.errorMessage.global;

  return (
    <Flex direction="column" gap={1}>
      <Flex gap={4}>
        <Flex className="max-w-xs">
          <Dropdown
            label={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.suite.label'
            )}
            fixedLabel
            testId="suiteDropdown"
            placeholder={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.suite.placeholder'
            )}
            noOptionsMessage={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.suite.noOptions'
            )}
            items={suiteDropdownItems}
            onChange={value => handleOnChangeSuite(value)}
            value={leaseItem.suiteId}
            doTranslation={false}
            invalid={Boolean(getErrorMessages('suiteId').length)}
            errors={getErrorMessages('suiteId')}
            isClearable
          />
        </Flex>
        <Flex className="max-w-xs">
          <Input
            fieldName="leaseId"
            label={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.externalLeaseId.label'
            )}
            fixedLabel
            testId="leaseIdInput"
            value={leaseItem.leaseId}
            error={getErrorMessages('leaseId')}
            onChange={value => handleOnChange('leaseId', value)}
            placeholder={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.externalLeaseId.placeholder'
            )}
          />
        </Flex>
        <Flex className="max-w-xs">
          <Dropdown
            label={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.externalSourceType.label'
            )}
            fixedLabel
            testId="sourceTypeDropdown"
            placeholder={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.externalSourceType.placeholder'
            )}
            noOptionsMessage={t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.externalSourceType.noOptions'
            )}
            items={externalIdSourceOptions}
            onChange={value => handleOnChange('sourceType', value)}
            value={leaseItem.sourceType}
            doTranslation={false}
            invalid={Boolean(getErrorMessages('sourceType').length)}
            errors={getErrorMessages('sourceType')}
            isClearable
          />
        </Flex>
        <Flex className="items-start">
          <Button
            disabled={!leaseItem.isRemovable}
            className="mt-4.5"
            startIcon={<Icon name="TrashAlt" />}
            variant="text"
            testId="remove-tenant-lease"
            onClick={handleRemove}
          />
        </Flex>
      </Flex>
      {globalErrorMessage && (
        <Flex className={styles.errorText}>{globalErrorMessage}</Flex>
      )}
    </Flex>
  );
};
